import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Space, Table, Tag, Typography } from 'antd';
import { ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { listActionPolicies, type ActionPolicyRow, type ActionPolicyVersion, type ActionPolicyView, type ActionTier } from '@/api/actionPolicies';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { ActionPolicyDrawer } from './ActionPolicyDrawer';

const { Text } = Typography;

// Agent Studio "Action policies" — which agent actions run, ask the person first, need a named role,
// or are never allowed; the platform default and the selected practice's override; who signed each
// version. Backed by ai.AgentActionPolicy (vNEXT AgentActionPolicyController).

const tierTone = (tier?: ActionTier | null): PillTone =>
  tier == null ? 'slate' : tier === 0 ? 'green' : tier === 1 ? 'green' : tier === 2 ? 'amber' : tier === 3 ? 'violet' : 'red';

function VersionCell({ v, empty }: { v?: ActionPolicyVersion | null; empty: string }) {
  if (!v) return <Text type="secondary" style={{ fontSize: 12.5 }}>{empty}</Text>;
  return (
    <Space direction="vertical" size={2}>
      <StatusPill tone={v.isActive ? tierTone(v.tier) : 'slate'}>{v.isActive ? v.tierName : `Off · ${v.tierName}`}</StatusPill>
      <Text style={{ fontSize: 11.5, color: v.signed ? colors.slate[500] : colors.amber[700] }}>
        {v.signed ? <><SafetyCertificateOutlined /> Signed by {v.signedByName}</> : `Not signed · v${v.version}`}
      </Text>
    </Space>
  );
}

export const ActionPoliciesPage = observer(function ActionPoliciesPage() {
  const { merchantContextStore: ctx } = useStore();
  const merchantId = ctx.selectedMerchantId;
  const merchantName = ctx.selectedMerchant?.title ?? null;

  const [view, setView] = useState<ActionPolicyView | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openKind, setOpenKind] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await listActionPolicies(merchantId ?? 0);
      setView(result.view);
      setCanEdit(result.canEdit);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load action policies');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [merchantId]);

  const rows = view?.rows ?? [];
  const selected = rows.find((r) => r.kind === openKind) ?? null;
  const unsigned = rows.filter((r) => (r.platform && !r.platform.signed) || (r.practice && !r.practice.signed)).length;

  const columns: ColumnsType<ActionPolicyRow> = [
    {
      title: 'Action',
      key: 'action',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Text strong>{r.label}</Text>
          <Space size={4} wrap>
            <Text type="secondary" style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{r.kind}</Text>
            {!r.isChatTool && <Tag color="purple" style={{ fontSize: 11 }}>clinical request</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Platform default',
      key: 'platform',
      width: 200,
      render: (_, r) => <VersionCell v={r.platform} empty="No rule" />,
    },
    {
      title: merchantName ? `${merchantName}` : 'This practice',
      key: 'practice',
      width: 200,
      render: (_, r) => merchantId
        ? <VersionCell v={r.practice} empty="Uses the platform default" />
        : <Text type="secondary" style={{ fontSize: 12.5 }}>Pick a practice in the header</Text>,
    },
    {
      title: 'What happens today',
      key: 'today',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          {r.effectiveTierName
            ? <StatusPill tone={tierTone(r.effectiveTier)}>{r.effectiveTierName}</StatusPill>
            : <StatusPill tone="slate">No rule</StatusPill>}
          <Text style={{ fontSize: 12.5 }}>{r.today}</Text>
          {r.appliesFrom !== 'none' && (
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              From the {r.appliesFrom === 'practice' ? `${merchantName ?? 'practice'} rule` : 'platform default'}
            </Text>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<SafetyCertificateOutlined />}
        title={
          <Space size={6} align="center">
            <span>Action policies</span>
            <HelpHotspot topic="governance.action-policies" />
          </Space>
        }
        subtitle="Which actions an agent may take on its own, which ask the person first, and which are never allowed — for every practice, or one practice at a time."
        count={rows.length}
        countLabel={rows.length === 1 ? 'action' : 'actions'}
        actions={
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      {error && <Alert type="error" showIcon message="Failed to load action policies" description={error} />}
      {!merchantId && (
        <Alert type="info" showIcon message="Showing the platform defaults. Pick a practice in the header to see and change its own rules." />
      )}
      {unsigned > 0 && (
        <Alert type="warning" showIcon
          message={`${unsigned} ${unsigned === 1 ? 'action has a rule' : 'actions have rules'} nobody has signed yet.`}
          description="Unsigned rules still apply. Open one to review it and sign the version you approve." />
      )}

      <SectionCard noPadding>
        <Table<ActionPolicyRow>
          rowKey="kind"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          onRow={(r) => ({ onClick: () => setOpenKind(r.kind), style: { cursor: 'pointer' } })}
        />
      </SectionCard>

      <ActionPolicyDrawer
        open={!!selected}
        row={selected}
        view={view}
        canEdit={canEdit}
        merchantId={merchantId}
        merchantName={merchantName}
        onClose={() => setOpenKind(null)}
        onChanged={() => void load()}
      />
    </div>
  );
});
